import { useCompanyStore } from '../stores/company';
import { useSessionStore } from '../stores/session';
import { addMoney, parseAmountInput, ZERO, subMoney } from './money';
import type {
  AccountGroup,
  ApiErrorBody,
  Branch,
  Company,
  CompanyFeatures,
  BusinessActivity,
  CompanyRef,
  FinancialYear,
  Ledger,
  PeriodLock,
  PermissionCatalogueEntry,
  Role,
  Restrictions,
  AuditEntry,
  SessionPayload,
  SystemHealth,
  User,
  UserAssignment,
  UserDetail,
  UserOverride,
  CancelVoucherPayload,
  CreateVoucherPayload,
  UpdateVoucherPayload,
  Voucher,
  VoucherListParams,
  VoucherType,
  BalanceSheetReport,
  DayBookReport,
  GroupSummaryReport,
  LedgerReport,
  ReportCode,
  ReportPreference,
  ReportQuery,
  TrialBalanceReport,
  Item,
  ItemGroup,
  UnitOfMeasure,
  HsnSacRate,
  PartyAddress,
  VoucherClass,
  InvoiceSeries,
  InvoiceNumberingState,
  GoodsPostingAccountMapping,
  PrintTemplate,
  ItemWritePayload,
  MasterWrite,
  ItemMasterWritePayload,
  ItemUnitWritePayload,
  ItemIdentifierWritePayload,
  ItemGstRateWritePayload,
  ItemIdentifier,
  GoodsDirection,
  GoodsVoucher,
  GoodsVoucherPayload,
  GoodsVoucherPreview,
  GoodsVoucherRegister,
  MoneyVoucher,
  MoneyVoucherPayload,
  MoneyVoucherCreatePayload,
  OutstandingReport,
  OutstandingBill,
  OutstandingParty,
  InterestPreview,
  BookReport,
  PdcEntry,
  ReconciliationReport,
  ReconciliationMatchCandidate,
  BankMappingProfile,
  BankStatementLine,
  ReconciliationMutationPayload,
  MoneyVoucherKind,
  BankMappingProfileWrite,
  BankReconciliationSettings,
} from './types';

/**
 * The ONLY place `fetch` appears in this app (CONVENTIONS.md §6). Every
 * screen calls the typed helpers exported below instead of touching the
 * network directly.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Record<string, unknown>;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    // Do not assign to Error#name here. Some embedded/browser runtimes expose
    // that inherited property as read-only; the subclass identity is already
    // preserved by instanceof ApiError and callers use the typed fields below.
    this.status = status;
    this.code = body.code;
    this.details = body.details;
  }

  /** 409 REVISION_CONFLICT — someone else changed this row first. */
  get isRevisionConflict(): boolean {
    return this.status === 409 && this.code === 'REVISION_CONFLICT';
  }
}

const MOCK_ENABLED = import.meta.env.VITE_MOCK === '1';

interface RequestOptions {
  query?: Record<string, string | number | boolean | undefined | null>;
  body?: unknown;
  idempotencyKey?: string;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(`/api/v1${path}`, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
    }
  }
  return url.pathname + url.search;
}

function onUnauthorized() {
  useSessionStore.getState().clear();
  if (window.location.pathname !== '/login') {
    // Hard redirect: guarantees no stale in-memory state survives an
    // expired/invalidated session.
    window.location.href = '/login';
  }
}

async function rawRequest<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const url = buildUrl(path, opts.query);

  const activeCompanyId = useCompanyStore.getState().activeCompanyId;

  if (MOCK_ENABLED) {
    const { mockFetch } = await import('./mockServer');
    return mockFetch<T>(method, url, opts.body, activeCompanyId, opts.idempotencyKey);
  }

  const headers: Record<string, string> = {};
  if (activeCompanyId) headers['X-Company-Id'] = activeCompanyId;
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;

  const res = await fetch(url, {
    method,
    headers,
    credentials: 'include',
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  if (res.status === 401) {
    onUnauthorized();
    throw new ApiError(401, {
      code: 'UNAUTHORIZED',
      message: 'Your session has expired. Please log in again.',
    });
  }

  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;

  if (!res.ok) {
    const body: ApiErrorBody = data?.error ?? {
      code: 'UNKNOWN_ERROR',
      message: `Request failed with status ${res.status}.`,
    };
    throw new ApiError(res.status, body);
  }

  return data as T;
}

export interface DownloadedFile {
  filename: string;
  contentType: string;
  blob: Blob;
}

function filenameFromDisposition(header: string | null, fallback: string): string {
  const matched = header?.match(/filename="?([^";]+)"?/i);
  return matched?.[1] ?? fallback;
}

/** Binary downloads stay outside JSON parsing. The mock returns the same `{ blob, filename }` shape. */
async function download(path: string, query: RequestOptions['query'], fallback: string): Promise<DownloadedFile> {
  const url = buildUrl(path, query);
  const activeCompanyId = useCompanyStore.getState().activeCompanyId;
  if (MOCK_ENABLED) {
    const { mockFetch } = await import('./mockServer');
    return mockFetch<DownloadedFile>('GET', url, undefined, activeCompanyId);
  }
  const headers: Record<string, string> = {};
  if (activeCompanyId) headers['X-Company-Id'] = activeCompanyId;
  const res = await fetch(url, {
    method: 'GET',
    headers,
    credentials: 'include',
  });
  if (res.status === 401) {
    onUnauthorized();
    throw new ApiError(401, {
      code: 'UNAUTHORIZED',
      message: 'Your session has expired. Please log in again.',
    });
  }
  if (!res.ok) {
    const text = await res.text();
    const data = text ? JSON.parse(text) : undefined;
    throw new ApiError(
      res.status,
      data?.error ?? {
        code: 'UNKNOWN_ERROR',
        message: `Download failed with status ${res.status}.`,
      }
    );
  }
  return {
    filename: filenameFromDisposition(res.headers.get('content-disposition'), fallback),
    contentType: res.headers.get('content-type') ?? 'application/octet-stream',
    blob: await res.blob(),
  };
}

const http = {
  get: <T>(path: string, opts?: RequestOptions) => rawRequest<T>('GET', path, opts),
  post: <T>(path: string, opts?: RequestOptions) => rawRequest<T>('POST', path, opts),
  patch: <T>(path: string, opts?: RequestOptions) => rawRequest<T>('PATCH', path, opts),
  put: <T>(path: string, opts?: RequestOptions) => rawRequest<T>('PUT', path, opts),
  delete: <T>(path: string, opts?: RequestOptions) => rawRequest<T>('DELETE', path, opts),
};

export interface ListResponse<T> {
  items: T[];
  nextCursor: string | null;
}

export interface CreateCompanyPayload {
  name: string;
  legalName?: string | null;
  pan?: string | null;
  entityType: Company['entityType'];
  booksBeginOn: string;
  businessActivities: BusinessActivity[];
  businessProfile?: CompanyFeatures['businessProfile'];
  baseCurrency?: string;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  stateCode?: string | null;
  pincode?: string | null;
  country?: string;
  phone?: string | null;
  email?: string | null;
  primaryBranch: {
    name: string;
    gstin?: string | null;
    stateCode: string;
    registrationType: Branch['registrationType'];
    addressLine1?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    pincode?: string | null;
  };
  featureOverrides?: Partial<CompanyFeatures['flags']>;
}

export interface CreatedCompany {
  company: Company;
  features: CompanyFeatures;
  primaryBranchId: string;
  financialYearId: string;
}

export interface UpdateCompanyPayload {
  revision: string;
  name?: string;
  legalName?: string | null;
  pan?: string | null;
  entityType?: Company['entityType'];
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  stateCode?: string | null;
  pincode?: string | null;
  country?: string;
  phone?: string | null;
  email?: string | null;
  isActive?: boolean;
}

export interface UpdateCompanyFeaturesPayload {
  revision: string;
  businessActivities?: BusinessActivity[];
  businessProfile?: CompanyFeatures['businessProfile'];
  flags?: Partial<CompanyFeatures['flags']>;
  taxRoundingLevel?: CompanyFeatures['taxRoundingLevel'];
  allowNegativeStock?: boolean;
  costingMethod?: CompanyFeatures['costingMethod'];
}

export interface CreateFinancialYearPayload {
  startsOn?: string;
  endsOn?: string;
  label?: string;
}

export interface CreatePeriodLockPayload {
  lockedFrom: string;
  lockedTo: string;
  reason?: string | null;
}

export interface CreateUserPayload {
  email: string;
  fullName?: string | null;
  password: string;
  isActive?: boolean;
  mustChangePassword?: boolean;
}

export interface UpdateUserPayload {
  revision: string;
  email?: string;
  fullName?: string | null;
  isActive?: boolean;
}

export interface UserRestrictionsPayload {
  companyId?: string | null;
  maxBackdateDays?: number | null;
  maxForwarddateDays?: number | null;
  voucherAmountCeiling?: string | null;
  maskPurchaseRate?: boolean;
  maskCostAndMargin?: boolean;
}

export interface CreateBranchPayload {
  name: string;
  gstin?: string | null;
  stateCode: string;
  registrationType: Branch['registrationType'];
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  pincode?: string | null;
}

export interface UpdateBranchPayload extends Partial<CreateBranchPayload> {
  revision: string;
  isActive?: boolean;
}

export interface CreateAccountGroupPayload {
  name: string;
  parentId?: string | null;
  code?: string | null;
  nature?: AccountGroup['nature'];
  affectsGrossProfit?: boolean;
  sortOrder?: number | null;
}

export interface UpdateAccountGroupPayload extends Omit<Partial<CreateAccountGroupPayload>, 'nature'> {
  revision: string;
}

export type LedgerWritePayload = Pick<Ledger, 'name' | 'groupId' | 'ledgerType'> &
  Partial<Omit<Ledger, 'id' | 'companyId' | 'isSystem' | 'revision'>>;

export interface UpdateLedgerPayload extends LedgerWritePayload {
  revision: string;
}

export interface CreateRolePayload {
  code: string;
  name: string;
  description?: string | null;
  permissions?: string[];
}

export interface UpdateRolePayload {
  revision: string;
  code?: string;
  name?: string;
  description?: string | null;
}

// ---------------------------------------------------------------- auth ----

export const authApi = {
  login: (email: string, password: string) => http.post<SessionPayload>('/auth/login', { body: { email, password } }),
  logout: () => http.post<void>('/auth/logout'),
  session: () => http.get<SessionPayload>('/auth/session'),
  changePassword: (currentPassword: string, newPassword: string) =>
    http.post<void>('/auth/change-password', {
      body: { currentPassword, newPassword },
    }),
};

// ------------------------------------------------------------ companies ----

export const companiesApi = {
  list: () => http.get<ListResponse<CompanyRef>>('/companies'),
  create: (payload: CreateCompanyPayload) => http.post<CreatedCompany>('/companies', { body: payload }),
  get: (id: string) => http.get<Company>(`/companies/${id}`),
  update: (id: string, payload: UpdateCompanyPayload) => http.patch<Company>(`/companies/${id}`, { body: payload }),
  getFeatures: (id: string) => http.get<CompanyFeatures>(`/companies/${id}/features`),
  putFeatures: (id: string, features: UpdateCompanyFeaturesPayload) =>
    http.put<CompanyFeatures>(`/companies/${id}/features`, { body: features }),
};

export const branchesApi = {
  list: () => http.get<ListResponse<Branch>>('/branches'),
  create: (payload: CreateBranchPayload) => http.post<Branch>('/branches', { body: payload }),
  update: (id: string, payload: UpdateBranchPayload) => http.patch<Branch>(`/branches/${id}`, { body: payload }),
};

export const financialYearsApi = {
  list: () => http.get<ListResponse<FinancialYear>>('/financial-years'),
  create: (payload: CreateFinancialYearPayload = {}) => http.post<FinancialYear>('/financial-years', { body: payload }),
  close: (id: string, revision: string) =>
    http.post<FinancialYear>(`/financial-years/${id}/close`, {
      body: { revision },
    }),
};

export const periodLocksApi = {
  list: () => http.get<ListResponse<PeriodLock>>('/period-locks'),
  create: (payload: CreatePeriodLockPayload) => http.post<PeriodLock>('/period-locks', { body: payload }),
  delete: (id: string) => http.delete<void>(`/period-locks/${id}`),
};

// ------------------------------------------------------ chart of accounts ----

export const accountGroupsApi = {
  tree: () => http.get<ListResponse<AccountGroup>>('/account-groups'),
  flat: () =>
    http.get<ListResponse<AccountGroup>>('/account-groups', {
      query: { flat: true },
    }),
  create: (payload: CreateAccountGroupPayload) => http.post<AccountGroup>('/account-groups', { body: payload }),
  update: (id: string, payload: UpdateAccountGroupPayload) =>
    http.patch<AccountGroup>(`/account-groups/${id}`, { body: payload }),
  delete: (id: string) => http.delete<void>(`/account-groups/${id}`),
};

export const ledgersApi = {
  list: (
    params: {
      q?: string;
      groupId?: string;
      ledgerType?: string;
      cursor?: string;
      limit?: number;
    } = {}
  ) => http.get<ListResponse<Ledger>>('/ledgers', { query: params }),
  get: (id: string) => http.get<Ledger>(`/ledgers/${id}`),
  create: (payload: LedgerWritePayload) => http.post<Ledger>('/ledgers', { body: payload }),
  update: (id: string, payload: UpdateLedgerPayload) => http.patch<Ledger>(`/ledgers/${id}`, { body: payload }),
  delete: (id: string) => http.delete<void>(`/ledgers/${id}`),
};

// ------------------------------------------------------ phase 2 masters ----
// Keep endpoint wrappers deliberately small. The server owns validation and
// rate resolution; callers only exchange decimal strings and typed records.
function masterResource<T, W>(path: string) {
  return {
    list: (
      params: {
        q?: string;
        partyLedgerId?: string;
        branchId?: string;
        voucherTypeId?: string;
        includeInactive?: boolean;
        limit?: number;
        cursor?: string;
      } = {}
    ) => http.get<ListResponse<T>>(path, { query: params }),
    create: (payload: W) => http.post<T>(path, { body: payload }),
    update: (id: string, payload: Partial<W> & { revision: string }) =>
      http.patch<T>(`${path}/${id}`, { body: payload }),
  };
}

export const itemGroupsApi = masterResource<ItemGroup, MasterWrite<ItemGroup>>('/item-groups');
export const unitsOfMeasureApi = masterResource<UnitOfMeasure, MasterWrite<UnitOfMeasure>>('/units-of-measure');
export const hsnSacRatesApi = masterResource<HsnSacRate, MasterWrite<HsnSacRate>>('/hsn-sac-rates');
export const partyAddressesApi = masterResource<PartyAddress, MasterWrite<PartyAddress>>('/party-addresses');
export const voucherClassesApi = masterResource<VoucherClass, MasterWrite<VoucherClass>>('/voucher-classes');
export const invoiceSeriesApi = masterResource<InvoiceSeries, MasterWrite<InvoiceSeries>>('/invoice-series');
export const printTemplatesApi = masterResource<PrintTemplate, MasterWrite<PrintTemplate>>('/print-templates');
/** Server item row; `Item` below is the screen's assembled setup view. */
type RawItem = {
  id: string;
  companyId: string;
  itemGroupId: string | null;
  code: string | null;
  name: string;
  description: string;
  itemKind: Item['kind'];
  hsnSacCode: string | null;
  hsnSacKind: 'hsn' | 'sac' | null;
  standardPurchaseRate: string | null;
  standardSalesRate: string | null;
  actualVsBilledQtyEnabled: boolean;
  isActive: boolean;
  revision: string;
};
type RawItemUnit = {
  id: string;
  revision: string;
  unitId: string;
  conversionToStock: string | null;
  isStockUnit: boolean;
  isPurchaseUnit: boolean;
  isSalesUnit: boolean;
  /** Human-readable UOM facts returned alongside the opaque item-unit key. */
  unitName?: string;
  unitSymbol?: string;
  unitCode?: string;
};
type RawIdentifier = {
  id: string;
  revision: string;
  identifierType: ItemIdentifierWritePayload['identifierType'];
  value: string;
};
type RawRate = {
  id: string;
  revision: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  taxTreatment: Item['gstRates'][number]['taxTreatment'];
  gstRate: string;
  cessType: 'ad_valorem' | 'specific' | 'higher_of';
  cessRate: string;
  cessAmount: string | null;
};
function setupItem(
  base: RawItem,
  units: RawItemUnit[] = [],
  identifiers: RawIdentifier[] = [],
  rates: RawRate[] = []
): Item {
  const isZero = (value: string | null | undefined) => value === null || value === undefined || /^0(?:\.0+)?$/.test(value);
  const unitRows: Item['units'] = units.flatMap((u) => [
    ...(u.isStockUnit
      ? [
          {
            id: u.id,
            revision: u.revision,
            role: 'stock' as const,
            unitOfMeasureId: u.unitId,
            conversionFactor: u.conversionToStock ?? '1',
          },
        ]
      : []),
    ...(u.isPurchaseUnit
      ? [
          {
            id: u.id,
            revision: u.revision,
            role: 'purchase' as const,
            unitOfMeasureId: u.unitId,
            conversionFactor: u.conversionToStock ?? '1',
          },
        ]
      : []),
    ...(u.isSalesUnit
      ? [
          {
            id: u.id,
            revision: u.revision,
            role: 'sales' as const,
            unitOfMeasureId: u.unitId,
            conversionFactor: u.conversionToStock ?? '1',
          },
        ]
      : []),
  ]);
  return {
    ...base,
    alias: base.code,
    kind: base.itemKind,
    baseUnitId: unitRows.find((u) => u.role === 'stock')?.unitOfMeasureId ?? null,
    actualVsBilledQuantity: base.actualVsBilledQtyEnabled,
    units: unitRows,
    identifiers: identifiers.map((x) => ({
      id: x.id,
      revision: x.revision,
      kind: x.identifierType,
      value: x.value,
    })),
    gstRates: rates.map((x) => ({
      id: x.id,
      revision: x.revision,
      effectiveFrom: x.effectiveFrom,
      effectiveTo: x.effectiveTo,
      taxTreatment: x.taxTreatment,
      ratePercentage: x.gstRate,
      // The persisted zero ad-valorem shape is the API's canonical “no cess” representation.
      // Do not turn that into the misleading “Percentage 0.0000” in the editor.
      cessMode: isZero(x.cessRate) && isZero(x.cessAmount) ? null : x.cessType === 'ad_valorem' ? 'percentage' : x.cessType === 'specific' ? 'per_unit' : 'higher_of',
      cessPercentage: isZero(x.cessRate) && isZero(x.cessAmount) ? null : x.cessRate,
      cessPerUnit: isZero(x.cessRate) && isZero(x.cessAmount) ? null : x.cessAmount,
    })),
  };
}
function itemMasterPayload(input: ItemWritePayload): ItemMasterWritePayload {
  return {
    name: input.name,
    code: input.alias ?? undefined,
    itemGroupId: input.itemGroupId,
    itemKind: input.kind,
    hsnSacCode: input.hsnSacCode,
    hsnSacKind: input.hsnSacCode ? (input.kind === 'goods' ? 'hsn' : 'sac') : null,
    standardPurchaseRate: input.standardPurchaseRate,
    standardSalesRate: input.standardSalesRate,
    actualVsBilledQtyEnabled: input.actualVsBilledQuantity,
    isActive: input.isActive,
  };
}
function ratePayload(rate: Item['gstRates'][number]): ItemGstRateWritePayload {
  return {
    effectiveFrom: rate.effectiveFrom,
    effectiveTo: rate.effectiveTo,
    taxTreatment: rate.taxTreatment,
    gstRate: rate.ratePercentage || '0',
    cessType:
      rate.cessMode === 'percentage'
        ? 'ad_valorem'
        : rate.cessMode === 'per_unit'
          ? 'specific'
          : (rate.cessMode ?? 'ad_valorem'),
    cessRate: rate.cessPercentage ?? '0',
    cessAmount: rate.cessPerUnit,
  };
}
function itemSetupPayload(input: ItemWritePayload) {
  const coalesced = new Map<
    string,
    {
      id?: string;
      revision?: string;
      unitId: string;
      conversionToStock: string;
      roles: Array<'stock' | 'purchase' | 'sales'>;
    }
  >();
  const add = (
    unitId: string,
    role: 'stock' | 'purchase' | 'sales',
    conversionToStock: string,
    id?: string,
    revision?: string
  ) => {
    if (!unitId) return;
    const row = coalesced.get(unitId) ?? {
      id,
      revision,
      unitId,
      conversionToStock,
      roles: [],
    };
    if (!row.roles.includes(role)) row.roles.push(role);
    row.id ??= id;
    row.revision ??= revision;
    coalesced.set(unitId, row);
  };
  if (input.kind === 'goods' && input.baseUnitId) add(input.baseUnitId, 'stock', '1.000000');
  input.units.forEach((unit) => {
    if (unit.role !== 'base') add(unit.unitOfMeasureId, unit.role, unit.conversionFactor, unit.id, unit.revision);
  });
  return {
    item: itemMasterPayload(input),
    units: [...coalesced.values()],
    identifiers: input.identifiers
      .filter((x) => x.value.trim())
      .map((x) => ({
        id: x.id,
        revision: x.revision,
        identifierType: x.kind,
        value: x.value,
      })),
    gstRates: input.gstRates.map((x) => ({
      id: x.id,
      revision: x.revision,
      ...ratePayload(x),
    })),
  };
}
async function itemDetail(id: string): Promise<Item> {
  const base = await http.get<RawItem>(`/items/${id}`);
  const [units, identifiers, rates] = await Promise.all([
    http.get<RawItemUnit[]>(`/items/${id}/units`),
    http.get<RawIdentifier[]>(`/items/${id}/identifiers`),
    http.get<RawRate[]>(`/items/${id}/gst-rates`),
  ]);
  return setupItem(base, units, identifiers, rates);
}
export const itemsApi = {
  list: async (params: { q?: string } = {}) => {
    const response = await http.get<ListResponse<RawItem>>('/items', {
      query: params,
    });
    return {
      ...response,
      items: response.items.map((item) => setupItem(item)),
    };
  },
  get: itemDetail,
  async create(input: ItemWritePayload) {
    const saved = await http.post<
      RawItem & {
        units: RawItemUnit[];
        identifiers: RawIdentifier[];
        gstRates: RawRate[];
      }
    >('/items/setup', { body: itemSetupPayload(input) });
    return setupItem(saved, saved.units, saved.identifiers, saved.gstRates);
  },
  async update(id: string, input: ItemWritePayload & { revision: string }) {
    const saved = await http.patch<
      RawItem & {
        units: RawItemUnit[];
        identifiers: RawIdentifier[];
        gstRates: RawRate[];
      }
    >(`/items/${id}/setup`, {
      body: { revision: input.revision, ...itemSetupPayload(input) },
    });
    return setupItem(saved, saved.units, saved.identifiers, saved.gstRates);
  },
  units: {
    list: (itemId: string) => http.get<RawItemUnit[]>(`/items/${itemId}/units`),
    create: (itemId: string, payload: ItemUnitWritePayload) =>
      http.post<RawItemUnit>(`/items/${itemId}/units`, { body: payload }),
  },
  identifiers: {
    list: (itemId: string) => http.get<RawIdentifier[]>(`/items/${itemId}/identifiers`),
    create: (itemId: string, payload: ItemIdentifierWritePayload) =>
      http.post<RawIdentifier>(`/items/${itemId}/identifiers`, {
        body: payload,
      }),
  },
  gstRates: {
    list: (itemId: string) => http.get<RawRate[]>(`/items/${itemId}/gst-rates`),
    create: (itemId: string, payload: ItemGstRateWritePayload) =>
      http.post<RawRate>(`/items/${itemId}/gst-rates`, { body: payload }),
  },
};
export const invoiceNumberingStatesApi = {
  list: (invoiceSeriesId?: string) =>
    http.get<InvoiceNumberingState[]>('/invoice-numbering-states', {
      query: { invoiceSeriesId },
    }),
};
export const goodsPostingAccountMappingApi = {
  get: () => http.get<GoodsPostingAccountMapping>('/goods-posting-account-mapping'),
  update: (payload: Omit<GoodsPostingAccountMapping, 'id' | 'companyId'>) =>
    http.patch<GoodsPostingAccountMapping>('/goods-posting-account-mapping', {
      body: payload,
    }),
};

// Goods voucher totals/GST are computed by the server. The UI never derives
// tax components from rates or assumes that an entered line will post.
export const goodsVouchersApi = {
  create: async (direction: GoodsDirection, payload: GoodsVoucherPayload, idempotencyKey: string) =>
    normaliseGoodsVoucher(
      await http.post<unknown>(`/goods-vouchers/${direction}`, {
        body: payload,
        idempotencyKey,
      })
    ),
  /** Physical returns use dedicated routes so a financial-only note cannot move stock. */
  createPhysicalReturn: async (
    kind: 'debit_note' | 'credit_note',
    payload: GoodsVoucherPayload,
    idempotencyKey: string
  ) =>
    normaliseGoodsVoucher(
      await http.post<unknown>(`/goods-vouchers/${kind.replace('_', '-')}`, {
        body: payload,
        idempotencyKey,
      })
    ),
  get: async (id: string) => normaliseGoodsVoucher(await http.get<unknown>(`/goods-vouchers/${id}`)),
  cancel: async (id: string, revision: string, reason: string) =>
    normaliseGoodsVoucher(
      await http.post<unknown>(`/goods-vouchers/${id}/cancel`, {
        body: { revision, reason },
      })
    ),
  /** Optional backend capability: callers must gracefully hide preview if absent. */
  preview: async (
    direction: GoodsDirection,
    payload: GoodsVoucherPayload,
    signal?: AbortSignal
  ): Promise<GoodsVoucherPreview> => {
    const raw = await http.post<unknown>(`/goods-vouchers/${direction}/preview`, { body: payload, signal });
    const value = (raw ?? {}) as Record<string, any>;
    const computed =
      value.computed ??
      (value.computation
        ? {
            direction: value.direction ?? direction,
            computation: value.computation,
            lineFacts: value.normalizedFacts?.lines,
            additionalCharges: value.normalizedFacts?.additionalCharges,
          }
        : value);
    return { preview: true, computed };
  },
  previewPhysicalReturn: async (
    kind: 'debit_note' | 'credit_note',
    payload: GoodsVoucherPayload,
    signal?: AbortSignal
  ): Promise<GoodsVoucherPreview> => {
    const raw = await http.post<unknown>(`/goods-vouchers/${kind.replace('_', '-')}/preview`, { body: payload, signal });
    const value = (raw ?? {}) as Record<string, any>;
    const computed =
      value.computed ??
      (value.computation
        ? {
            direction: kind === 'debit_note' ? 'purchase' : 'sales',
            computation: value.computation,
            lineFacts: value.normalizedFacts?.lines,
            additionalCharges: value.normalizedFacts?.additionalCharges,
          }
        : value);
    return { preview: true, computed };
  },
  register: (direction: GoodsDirection, from: string, to: string) =>
    http.get<GoodsVoucherRegister>(`/goods-vouchers/registers/${direction}`, {
      query: { from, to },
    }),
  exportRegister: (direction: GoodsDirection, from: string, to: string, format: 'xlsx' | 'pdf') =>
    download(`/goods-vouchers/registers/${direction}/export`, { from, to, format }, `${direction}-register.${format}`),
  print: (id: string, copy?: 'Original for Recipient' | 'Duplicate for Transporter' | 'Triplicate for Supplier') =>
    download(`/goods-vouchers/${id}/print`, { copy }, `invoice-${id}.pdf`),
};

/**
 * Goods endpoints return direct SQL rows today while the rest of the client
 * speaks camelCase. Keep the compatibility boundary here so screens do not
 * accidentally couple themselves to database column names.
 */
function normaliseGoodsVoucher(raw: unknown): GoodsVoucher {
  const value = (raw ?? {}) as Record<string, any>;
  const mapFinancial = (entry: Record<string, any>) => ({
    id: String(entry.id),
    ledgerId: String(entry.ledgerId ?? entry.ledger_id ?? ''),
    lineNumber: Number(entry.lineNumber ?? entry.line_number ?? 0),
    debit: String(entry.debit ?? '0.0000'),
    credit: String(entry.credit ?? '0.0000'),
    narration: String(entry.narration ?? ''),
    isActive: Boolean(entry.isActive ?? entry.is_active ?? false),
  });
  const mapBill = (entry: Record<string, any>) => ({
    id: entry.id ? String(entry.id) : undefined,
    referenceType: entry.referenceType ?? entry.reference_type,
    referenceNumber: entry.referenceNumber ?? entry.reference_number ?? undefined,
    settlesBillReferenceId: entry.settlesBillReferenceId ?? entry.settles_bill_reference_id ?? undefined,
    amount: String(entry.amount ?? '0.0000'),
    dueDate: entry.dueDate ?? entry.due_date ?? undefined,
    narration: entry.narration ?? undefined,
  });
  return {
    id: String(value.id),
    revision: String(value.revision),
    companyId: String(value.companyId ?? value.company_id ?? ''),
    branchId: String(value.branchId ?? value.branch_id ?? ''),
    financialYearId: String(value.financialYearId ?? value.financial_year_id ?? ''),
    voucherTypeId: String(value.voucherTypeId ?? value.voucher_type_id ?? ''),
    voucherDate: String(value.voucherDate ?? value.voucher_date ?? ''),
    voucherNumber: String(value.voucherNumber ?? value.voucher_number ?? ''),
    displayNumber: String(value.displayNumber ?? value.display_number ?? ''),
    state: value.state,
    narration: String(value.narration ?? ''),
    reference: value.reference ?? null,
    cancelledAt: value.cancelledAt ?? value.cancelled_at ?? null,
    cancellationReason: value.cancellationReason ?? value.cancellation_reason ?? null,
    computed: value.computed ?? null,
    financialEntries: Array.isArray(value.financialEntries) ? value.financialEntries.map(mapFinancial) : [],
    inventoryEntries: Array.isArray(value.inventoryEntries) ? value.inventoryEntries : [],
    billReferences: Array.isArray(value.billReferences) ? value.billReferences.map(mapBill) : [],
  } as GoodsVoucher;
}

// -------------------------------------------------------- users / roles ----

export const permissionsApi = {
  catalogue: () => http.get<{ items: PermissionCatalogueEntry[] }>('/permissions'),
};

export const usersApi = {
  list: () => http.get<ListResponse<User>>('/users'),
  get: (id: string) => http.get<UserDetail>(`/users/${id}`),
  create: (payload: CreateUserPayload) => http.post<User>('/users', { body: payload }),
  update: (id: string, payload: UpdateUserPayload) => http.patch<User>(`/users/${id}`, { body: payload }),
  resetPassword: (id: string, temporaryPassword: string) =>
    http.post<{ ok: true; mustChangePassword: true }>(`/users/${id}/reset-password`, { body: { temporaryPassword } }),
  setRoles: (id: string, roles: UserAssignment[]) =>
    http.put<{ roles: UserAssignment[] }>(`/users/${id}/roles`, {
      body: { roles },
    }),
  setCompanies: (id: string, companyIds: string[]) =>
    http.put<{ companyIds: string[] }>(`/users/${id}/companies`, {
      body: { companyIds },
    }),
  setOverrides: (id: string, overrides: UserOverride[]) =>
    http.put<{ overrides: UserOverride[] }>(`/users/${id}/overrides`, {
      body: { overrides },
    }),
  setRestrictions: (id: string, restrictions: UserRestrictionsPayload) =>
    http.put<Restrictions>(`/users/${id}/restrictions`, { body: restrictions }),
};

export const rolesApi = {
  list: () => http.get<ListResponse<Role>>('/roles'),
  create: (payload: CreateRolePayload) => http.post<Role>('/roles', { body: payload }),
  update: (id: string, payload: UpdateRolePayload) => http.patch<Role>(`/roles/${id}`, { body: payload }),
  setPermissions: (id: string, permissions: string[]) =>
    http.put<Role>(`/roles/${id}/permissions`, { body: { permissions } }),
};

// -------------------------------------------------------------- audit ----

export const auditApi = {
  record: (table: string, recordId: string) =>
    http.get<{ items: AuditEntry[] }>('/audit/record', {
      query: { table, recordId },
    }),
  activity: (
    params: {
      from?: string;
      to?: string;
      userId?: string;
      table?: string;
      cursor?: string;
    } = {}
  ) => http.get<ListResponse<AuditEntry>>('/audit/activity', { query: params }),
};

// -------------------------------------------------------------- health ----

export const healthApi = {
  liveness: () =>
    http.get<{
      status: string;
      version: string;
      build: string;
      schemaVersion: string;
    }>('/health'),
  system: () => http.get<SystemHealth>('/system/health'),
};

// ---------------------------------------------------------- vouchers ----

export const voucherTypesApi = {
  list: () => http.get<ListResponse<VoucherType>>('/voucher-types'),
};

export const vouchersApi = {
  list: (params: VoucherListParams = {}) =>
    http.get<ListResponse<Voucher>>('/vouchers', {
      query: params as Record<string, string | number | boolean | undefined | null>,
    }),
  get: (id: string) => http.get<Voucher>(`/vouchers/${id}`),
  create: (payload: CreateVoucherPayload, idempotencyKey: string) =>
    http.post<Voucher>('/vouchers', { body: payload, idempotencyKey }),
  update: (id: string, payload: UpdateVoucherPayload) => http.patch<Voucher>(`/vouchers/${id}`, { body: payload }),
  cancel: (id: string, payload: CancelVoucherPayload) =>
    http.post<Voucher>(`/vouchers/${id}/cancel`, { body: payload }),
};

// -------------------------------------------------------- phase 3 money ----
// These wrappers keep transport details out of the workspaces. The server
// accepts the same decimal-string amounts shown in the UI and enforces all
// balancing, bill-reference and optimistic-revision invariants.
function toBookReport(raw: any, book: 'cash' | 'bank'): BookReport {
  const ledgers = Array.isArray(raw.ledgers) ? raw.ledgers : [];
  const items = Array.isArray(raw.items) ? raw.items : [];
  const entries = items.map((item: any) => ({
    id: item.voucherId,
    voucherId: item.voucherId,
    voucherDate: item.voucherDate,
    displayNumber: item.displayNumber,
    ledgerName: ledgers.find((ledger: any) => ledger.id === item.ledgerId)?.name ?? '—',
    partyName: null,
    narration: item.narration ?? '',
    debit: item.debit,
    credit: item.credit,
    runningBalance: item.runningBalance,
    reconciled: item.reconciliationStatus === 'reconciled',
    reconciliationDate: null,
    instrumentReference: item.referenceNumber ?? item.instrumentNumber ?? null,
  }));
  const opening = raw.totals?.opening ?? ZERO;
  const closing = raw.totals?.closing ?? ZERO;
  const unreconciledMovement = items.filter((item: any) => item.reconciliationStatus !== 'reconciled').reduce((sum: string, item: any) => subMoney(addMoney(sum, item.debit), item.credit), ZERO);
  return { book, ledgerId: raw.ledgerId ?? paramsLedgerId(raw), ledgerName: ledgers.length === 1 ? ledgers[0].name : `${book === 'cash' ? 'Cash' : 'Bank'} book`, from: raw.from, to: raw.to, openingBalance: opening, closingBalance: closing, reconciledBalance: subMoney(closing, unreconciledMovement), unreconciledBalance: unreconciledMovement, negativeBalance: closing.startsWith('-'), entries };
}
function paramsLedgerId(raw: any): string { return raw.ledgers?.length === 1 ? raw.ledgers[0].id : ''; }

export const moneyVouchersApi = {
  list: (params: { kind?: MoneyVoucherKind; from?: string; to?: string; partyLedgerId?: string; limit?: number } = {}) =>
    http.get<ListResponse<MoneyVoucher>>('/money-vouchers', {
      query: { behaviour: params.kind, from: params.from, to: params.to, limit: params.limit },
    }),
  get: (id: string) => http.get<MoneyVoucher>(`/money-vouchers/${id}`),
  create: (kind: MoneyVoucherKind, payload: MoneyVoucherCreatePayload, idempotencyKey: string) =>
    http.post<MoneyVoucher>(`/money-vouchers/${kind}`, { body: payload, idempotencyKey }),
  update: (id: string, payload: { revision: string; narration?: string; reference?: string | null }) =>
    http.patch<MoneyVoucher>(`/money-vouchers/${id}`, { body: payload }),
  cancel: (id: string, payload: { revision: string; reason: string }) =>
    http.post<MoneyVoucher>(`/money-vouchers/${id}/cancel`, { body: payload }),
};

export const moneyReportsApi = {
  outstandings: async (params: { direction: 'receivable' | 'payable'; asOf?: string; ageBasis?: 'bill_date' | 'due_date'; buckets?: string }): Promise<OutstandingReport> => {
    const raw = await http.get<any>('/money/outstandings', { query: params });
    const items = Array.isArray(raw.items) ? raw.items : [];
    const billsByParty = new Map<string, OutstandingBill[]>();
    for (const item of items) {
      const bill: OutstandingBill = {
        id: item.id,
        partyLedgerId: item.partyLedgerId,
        partyName: item.partyName,
        billNumber: item.referenceNumber ?? item.id,
        billDate: item.voucherDate,
        dueDate: item.dueDate ?? null,
        originalAmount: item.amount,
        settledAmount: item.allocatedAmount,
        outstandingAmount: item.openAmount,
        overdueDays: item.overdueDays ?? 0,
        creditDays: null,
        lastContactedOn: null,
        followUpNote: null,
      };
      billsByParty.set(item.partyLedgerId, [...(billsByParty.get(item.partyLedgerId) ?? []), bill]);
    }
    const parties = (Array.isArray(raw.partyWise) ? raw.partyWise : []).map((party: any): OutstandingParty => {
      const bills = billsByParty.get(party.partyLedgerId) ?? [];
      const outstanding = bills.reduce((sum, bill) => addMoney(sum, bill.outstandingAmount), ZERO);
      const overdue = bills.filter((bill) => bill.overdueDays > 0).reduce((sum, bill) => addMoney(sum, bill.outstandingAmount), ZERO);
      return { partyLedgerId: party.partyLedgerId, partyName: party.partyName, billCount: bills.length, outstandingAmount: outstanding, overdueAmount: overdue, oldestDueOn: bills.map((bill) => bill.dueDate).filter(Boolean).sort()[0] ?? null, lastContactedOn: null, followUpNote: null, bills };
    });
    const ageing = Array.isArray(raw.ageing) ? raw.ageing : [];
    const outstanding = items.reduce((sum: string, item: any) => addMoney(sum, item.openAmount), ZERO);
    const overdue = items.filter((item: any) => Number(item.overdueDays ?? 0) > 0).reduce((sum: string, item: any) => addMoney(sum, item.openAmount), ZERO);
    return { asOf: raw.asOf ?? params.asOf ?? new Date().toISOString().slice(0, 10), side: params.direction, basis: raw.ageBasis ?? params.ageBasis ?? 'due_date', buckets: ageing.map((bucket: any) => ({ key: bucket.bucket, label: bucket.bucket, amount: bucket.amount, count: items.filter((item: any) => item.ageBucket === bucket.bucket).length })), parties, totals: { outstanding, overdue, current: subMoney(outstanding, overdue) } };
  },
  oldestFirst: (params: { partyLedgerId: string; direction: 'receivable' | 'payable'; amount: string; asOf: string }) =>
    http.get<{ partyLedgerId: string; direction: 'receivable' | 'payable'; requestedAmount: string; allocations: Array<{ partyLedgerId: string; referenceType: 'against_ref'; direction: 'receivable' | 'payable'; amount: string; settlesBillReferenceId: string; referenceNumber?: string | null }>; unallocatedAmount: string; remainder: { partyLedgerId: string; referenceType: 'on_account'; direction: 'receivable' | 'payable'; amount: string } | null }>('/money/allocations/oldest-first', { query: params }),
  cashBook: async (params: { ledgerId?: string; from?: string; to?: string } = {}) => toBookReport(await http.get<any>('/money/cash-book', { query: params }), 'cash'),
  bankBook: async (params: { ledgerId?: string; from?: string; to?: string } = {}) => toBookReport(await http.get<any>('/money/bank-book', { query: params }), 'bank'),
  pdcRegister: async (params: { direction?: 'receivable' | 'payable'; from?: string; to?: string } = {}): Promise<ListResponse<PdcEntry>> => {
    const { direction: _direction, ...query } = params;
    const raw = await http.get<any>('/money/pdc-register', { query });
    const items = (raw.items ?? []).map((item: any) => ({ id: item.id, direction: item.behaviourCode === 'receipt' ? 'receivable' : 'payable', partyLedgerId: item.partyLedgerId ?? '', partyName: item.parties ?? item.partyName ?? '—', chequeNumber: item.chequeNumber ?? '', chequeDate: item.instrumentDate, clearingDate: item.clearingDate, bankName: item.bankName, amount: item.amount ?? null, status: item.status === 'returned' ? 'bounced' : item.status, voucherId: item.voucherId, revision: item.voucherRevision ?? null }));
    return { nextCursor: raw.nextCursor ?? null, items: params.direction ? items.filter((item: PdcEntry) => item.direction === params.direction) : items };
  },
  clearPdc: (voucherId: string, revision: string, clearingDate: string) =>
    http.post<any>(`/money-vouchers/${voucherId}/clear`, { body: { revision, clearingDate } }),
  interestTerms: (partyLedgerId: string) => http.get<any>(`/money/interest/terms/${partyLedgerId}`),
  saveInterestTerms: (partyLedgerId: string, payload: { annualRate: string; calculationKind: 'simple' | 'compound'; graceDays: number; compoundInterval?: 'monthly' | 'quarterly' | 'annual' | null; incomeLedgerId?: string | null; expenseLedgerId?: string | null; isActive?: boolean; revision?: string }) => {
    const { revision, ...body } = payload;
    return http.put<any>(`/money/interest/terms/${partyLedgerId}`, { body: revision ? { ...body, revision } : body });
  },
  interestPreview: async (params: { partyLedgerId: string; asOf: string; direction?: 'receivable' | 'payable'; ratePercentage?: string; mode?: 'simple' | 'compound' }): Promise<InterestPreview> => {
    const raw = await http.get<any>('/money/interest/preview', { query: { partyLedgerId: params.partyLedgerId, asOf: params.asOf, direction: params.direction, ratePercentage: params.ratePercentage, mode: params.mode } });
    const item = raw.items?.[0];
    const interestAmount = item?.interest ?? ZERO;
    return { partyLedgerId: params.partyLedgerId, ratePercentage: item?.annualRate ?? params.ratePercentage ?? ZERO, mode: item?.calculationKind ?? params.mode ?? 'simple', from: item?.voucherDate ?? params.asOf ?? '', to: raw.asOf ?? params.asOf ?? '', baseAmount: item?.openAmount ?? ZERO, interestAmount, totalAmount: addMoney(item?.openAmount ?? ZERO, interestAmount) };
  },
  postInterest: (payload: { partyLedgerId: string; asOf: string; direction: 'receivable' | 'payable'; ratePercentage?: string; mode?: 'simple' | 'compound'; amount?: string; branchId?: string; financialYearId?: string }) =>
    http.post<MoneyVoucher>('/money/interest/post', { body: payload, idempotencyKey: crypto.randomUUID() }),
  updateFollowUp: (partyLedgerId: string, payload: { note: string; contactedOn: string; nextContactOn?: string | null }) =>
    http.post<void>(`/money/outstandings/${partyLedgerId}/follow-up`, { body: payload }),
};

export const bankReconciliationApi = {
  report: async (params: { bankLedgerId?: string; from?: string; to?: string } = {}): Promise<ReconciliationReport> => {
    if (!params.bankLedgerId || !params.to) {
      throw new ApiError(422, { code: 'INVALID_QUERY', message: 'Choose a bank account and reconciliation date first.' });
    }
    const raw = await http.get<any>('/bank-reconciliation/brs', {
      query: { bankLedgerId: params.bankLedgerId, asOf: params.to },
    });
    const statementId = raw.statementId ?? (await http.get<any>('/bank-reconciliation/statements', { query: { bankLedgerId: params.bankLedgerId, from: params.from, to: params.to, limit: 1 } })).items?.[0]?.id;
    const statement = statementId
      ? await http.get<any>(`/bank-reconciliation/statements/${statementId}`)
      : null;
    const statementLines = Array.isArray(statement?.lines) ? statement.lines : [];
    const lines = statementLines.map((line: any): BankStatementLine => ({
      id: line.id,
      matchId: line.matchId ?? null,
      revision: line.matchRevision ?? line.revision ?? null,
      fingerprint: line.fingerprint ?? '',
      transactionDate: line.transactionDate,
      valueDate: line.valueDate ?? null,
      reference: line.bankReference ?? '',
      description: line.description ?? '',
      debit: line.direction === 'debit' ? line.amount : ZERO,
      credit: line.direction === 'credit' ? line.amount : ZERO,
      balance: line.balance ?? null,
      status: line.unmatchedAmount === ZERO ? 'matched' : 'unmatched',
      matchConfidence: line.matchConfidence ?? null,
      matchedVoucherIds: line.matchedVoucherIds ?? [],
    }));
    const candidatesEntries = await Promise.all(
      lines.map(async (line: BankStatementLine) => {
        const rawCandidates = await http.get<any>(`/bank-reconciliation/lines/${line.id}/candidates`);
        const rows = Array.isArray(rawCandidates?.candidates) ? rawCandidates.candidates : [];
        return [line.id, rows.map((candidate: any): ReconciliationMatchCandidate => ({
          voucherId: candidate.id,
          displayNumber: candidate.displayNumber ?? candidate.id,
          voucherDate: candidate.voucherDate,
          narration: candidate.narration ?? '',
          amount: candidate.remainingAmount ?? candidate.bankAmount ?? ZERO,
          reference: line.reference || null,
          confidence: candidate.confidence ?? 'manual',
          differenceAmount: candidate.differenceAmount ?? ZERO,
          withinTolerance: candidate.withinTolerance ?? false,
          toleranceAmount: candidate.toleranceAmount ?? ZERO,
        }))] as const;
      })
    );
    return {
      bankLedgerId: raw.bankLedgerId ?? params.bankLedgerId,
      statementId: statementId ?? null,
      bankLedgerName: raw.bankName ?? 'Bank account',
      from: params.from ?? statement?.statementFrom ?? raw.asOf,
      to: params.to ?? raw.asOf,
      statementClosingBalance: raw.balanceAsPerStatement ?? ZERO,
      bookBalance: raw.balanceAsPerBooks ?? ZERO,
      reconciledBookBalance: raw.balanceAsPerBooks ?? ZERO,
      unclearedIssued: raw.add?.chequesIssuedNotPresented ?? ZERO,
      unclearedDeposits: raw.less?.chequesDepositedNotCleared ?? ZERO,
      directCredits: raw.add?.directCreditsNotRecorded ?? ZERO,
      bankCharges: raw.less?.bankChargesNotRecorded ?? ZERO,
      lines,
      candidates: Object.fromEntries(candidatesEntries),
      mappingProfiles: [],
      settings: raw.settings ?? null,
    };
  },
  reconciliationSettings: async (bankLedgerId: string) => {
    const raw = await http.get<any>(`/bank-reconciliation/settings/${bankLedgerId}`);
    return { id: raw.id ?? null, bankLedgerId: raw.bankLedgerId ?? bankLedgerId, toleranceAmount: raw.toleranceAmount ?? ZERO, bankChargesLedgerId: raw.bankChargesLedgerId ?? null, roundOffLedgerId: raw.roundOffLedgerId ?? null, revision: raw.revision ?? null } as BankReconciliationSettings;
  },
  saveReconciliationSettings: async (bankLedgerId: string, payload: { toleranceAmount: string; bankChargesLedgerId?: string | null; roundOffLedgerId?: string | null; revision?: string | null }) => {
    const raw = await http.put<any>(`/bank-reconciliation/settings/${bankLedgerId}`, { body: payload });
    return { id: raw.id ?? null, bankLedgerId: raw.bankLedgerId ?? bankLedgerId, toleranceAmount: raw.toleranceAmount ?? ZERO, bankChargesLedgerId: raw.bankChargesLedgerId ?? null, roundOffLedgerId: raw.roundOffLedgerId ?? null, revision: raw.revision ?? null } as BankReconciliationSettings;
  },
  profiles: async (bankLedgerId?: string): Promise<ListResponse<BankMappingProfile>> => {
    const raw = await http.get<any>('/bank-reconciliation/profiles', { query: bankLedgerId ? { bankLedgerId } : undefined });
    const rows = Array.isArray(raw) ? raw : raw.items ?? [];
    return { nextCursor: raw.nextCursor ?? null, items: rows.map((profile: any) => ({ id: profile.id, name: profile.name, bankLedgerId: profile.bankLedgerId, fileFormat: profile.fileFormat ?? 'csv', columnMapping: profile.columnMapping ?? {}, skipRows: profile.skipRows ?? 0, revision: profile.revision, dateColumn: profile.columnMapping?.date ?? '', referenceColumn: profile.columnMapping?.reference ?? '', descriptionColumn: profile.columnMapping?.description ?? '', debitColumn: profile.columnMapping?.debit ?? null, creditColumn: profile.columnMapping?.credit ?? null, amountColumn: profile.columnMapping?.amount ?? null, dateFormat: profile.dateFormat ?? '' })) };
  },
  saveProfile: async (payload: BankMappingProfileWrite) => {
    const body = { bankLedgerId: payload.bankLedgerId, name: payload.name, fileFormat: payload.fileFormat, columnMapping: payload.columnMapping, dateFormat: payload.dateFormat ?? null, skipRows: payload.skipRows ?? 0, ...(payload.revision ? { revision: payload.revision } : {}) };
    const raw = payload.id
      ? await http.patch<any>(`/bank-reconciliation/profiles/${payload.id}`, { body })
      : await http.post<any>('/bank-reconciliation/profiles', { body });
    return { id: raw.id, name: raw.name, bankLedgerId: raw.bankLedgerId, fileFormat: raw.fileFormat ?? payload.fileFormat, columnMapping: raw.columnMapping ?? payload.columnMapping, skipRows: raw.skipRows ?? payload.skipRows ?? 0, revision: raw.revision, dateColumn: raw.columnMapping?.date ?? String(payload.columnMapping.date ?? ''), referenceColumn: raw.columnMapping?.reference ?? String(payload.columnMapping.reference ?? ''), descriptionColumn: raw.columnMapping?.description ?? String(payload.columnMapping.description ?? ''), debitColumn: raw.columnMapping?.debit ?? null, creditColumn: raw.columnMapping?.credit ?? null, amountColumn: raw.columnMapping?.amount ?? null, dateFormat: raw.dateFormat ?? payload.dateFormat ?? '' } as BankMappingProfile;
  },
  importStatement: async (payload: { bankLedgerId: string; fileName: string; rows?: Array<Record<string, string>>; profileId?: string; sourceKind?: 'csv' | 'xlsx'; fileBase64?: string; columnMapping?: Record<string, unknown>; dateFormat?: string | null; skipRows?: number }) => {
    if (payload.fileBase64) {
      const raw = await http.post<any>('/bank-reconciliation/statements/import', { body: { bankLedgerId: payload.bankLedgerId, profileId: payload.profileId ?? null, sourceKind: payload.sourceKind ?? 'csv', sourceName: payload.fileName, fileBase64: payload.fileBase64, columnMapping: payload.columnMapping, dateFormat: payload.dateFormat ?? null, skipRows: payload.skipRows ?? 0 } });
      return { imported: raw.insertedCount ?? 0, duplicates: raw.duplicateCount ?? 0, errors: [], lines: [] as BankStatementLine[] };
    }
    const valueFor = (row: Record<string, string>, keys: string[]) => {
      const entries = Object.entries(row);
      const match = entries.find(([key]) => keys.some((candidate) => key.toLowerCase().replace(/\s+/g, '_').includes(candidate)));
      return match?.[1]?.trim() ?? '';
    };
    const lines = (payload.rows ?? []).map((row) => {
      const transactionDate = valueFor(row, ['transaction_date', 'txn_date', 'date', 'value_date']);
      const debit = parseAmountInput(valueFor(row, ['debit', 'withdrawal', 'withdrawals', 'dr'])) ?? ZERO;
      const credit = parseAmountInput(valueFor(row, ['credit', 'deposit', 'deposits', 'cr'])) ?? ZERO;
      const signed = parseAmountInput(valueFor(row, ['amount', 'transaction_amount']));
      const direction = debit !== ZERO ? 'debit' : 'credit';
      const amount = debit !== ZERO ? debit : credit !== ZERO ? credit : signed ? (signed.startsWith('-') ? signed.slice(1) : signed) : ZERO;
      return { transactionDate, valueDate: valueFor(row, ['value_date']) || null, direction: signed?.startsWith('-') ? 'debit' : direction, amount, bankReference: valueFor(row, ['reference', 'ref', 'utr', 'cheque', 'instrument']), description: valueFor(row, ['description', 'narration', 'particular']) };
    }).filter((line) => line.transactionDate && line.amount !== ZERO);
    const dates = lines.map((line) => line.transactionDate).sort();
    const raw = await http.post<any>('/bank-reconciliation/statements/import', { body: { bankLedgerId: payload.bankLedgerId, profileId: payload.profileId ?? null, sourceKind: payload.sourceKind ?? 'csv', sourceName: payload.fileName, statementFrom: dates[0] ?? null, statementTo: dates.at(-1) ?? null, lines } });
    return { imported: raw.insertedCount ?? 0, duplicates: raw.duplicateCount ?? 0, errors: [], lines: [] as BankStatementLine[] };
  },
  addManualLine: async (payload: { bankLedgerId: string; transactionDate: string; valueDate?: string | null; direction: 'debit' | 'credit'; amount: string; bankReference?: string | null; description?: string | null }) =>
    http.post<{ statementId: string; insertedCount: number; duplicateCount: number; sourceKind: string }>('/bank-reconciliation/statements/manual-lines', { body: { bankLedgerId: payload.bankLedgerId, sourceKind: 'manual', lines: [{ transactionDate: payload.transactionDate, valueDate: payload.valueDate ?? null, direction: payload.direction, amount: payload.amount, bankReference: payload.bankReference ?? null, description: payload.description ?? null, rawData: {} }] } }),
  candidates: (lineId: string) =>
    http.get<ListResponse<ReconciliationMatchCandidate>>(`/bank-reconciliation/lines/${lineId}/candidates`),
  confirmTolerance: (lineId: string, payload: { statementLineRevision: string; matches: Array<{ voucherId: string }> }, idempotencyKey: string) =>
    http.post<any>(`/bank-reconciliation/lines/${lineId}/confirm-tolerance`, { body: payload, idempotencyKey }),
  autoMatch: (statementId: string) =>
    http.post<{ autoMatched: unknown[]; autoMatchedCount: number }>(`/bank-reconciliation/statements/${statementId}/auto-match`),
  createVoucherForLine: (lineId: string, payload: MoneyVoucherCreatePayload, idempotencyKey: string) =>
    http.post<{ voucher: MoneyVoucher; match: { id: string; revision: string } }>(`/bank-reconciliation/lines/${lineId}/create-voucher`, { body: payload, idempotencyKey }),
  match: (matches: Array<{ bankStatementLineId: string; voucherId: string; amount: string; matchKind: 'auto' | 'probable' | 'manual'; confidence: 'exact' | 'probable' | 'manual' }>) =>
    http.post<{ items: Array<{ id: string; bankStatementLineId: string; voucherId: string; amount: string; matchKind: string; confidence: string; reconciledAt: string; revision: string }> }>('/bank-reconciliation/matches', { body: { matches } }),
  unmatch: (payload: { matchId: string; revision: string; reason: string }) =>
    http.post<{ id: string; isActive: boolean }>(`/bank-reconciliation/matches/${payload.matchId}/unreconcile`, { body: { revision: payload.revision, reason: payload.reason } }),
};

// ------------------------------------------------------------ reports ----

type ReportExportFormat = 'xlsx' | 'pdf';
type ReportQueryParams = ReportQuery & {
  format?: ReportExportFormat;
  ledgerId?: string;
};
const reportQuery = (
  query: ReportQuery | ReportQueryParams
): Record<string, string | number | boolean | undefined | null> =>
  query as Record<string, string | number | boolean | undefined | null>;

export const reportsApi = {
  dayBook: (query: ReportQuery = {}) => http.get<DayBookReport>('/reports/day-book', { query: reportQuery(query) }),
  trialBalance: (query: ReportQuery = {}) =>
    http.get<TrialBalanceReport>('/reports/trial-balance', {
      query: reportQuery(query),
    }),
  ledger: (ledgerId: string, query: ReportQuery = {}) =>
    http.get<LedgerReport>('/reports/ledger', {
      query: reportQuery({ ...query, ledgerId }),
    }),
  groupSummary: (query: ReportQuery = {}) =>
    http.get<GroupSummaryReport>('/reports/group-summary', {
      query: reportQuery(query),
    }),
  balanceSheet: (query: ReportQuery = {}) =>
    http.get<BalanceSheetReport>('/reports/balance-sheet', {
      query: reportQuery(query),
    }),
  preference: (reportCode: ReportCode) => http.get<ReportPreference>(`/reports/preferences/${reportCode}`),
  savePreference: (reportCode: ReportCode, preferences: Record<string, unknown>, expectedRevision: string | null) =>
    http.put<ReportPreference>(`/reports/preferences/${reportCode}`, {
      body: { preferences, expectedRevision },
    }),
  export: (reportCode: ReportCode, format: ReportExportFormat, query: ReportQueryParams = {}) => {
    const path =
      reportCode === 'day_book'
        ? '/reports/day-book/export'
        : reportCode === 'trial_balance'
          ? '/reports/trial-balance/export'
          : reportCode === 'ledger'
            ? '/reports/ledger/export'
            : reportCode === 'group_summary'
              ? '/reports/group-summary/export'
              : '/reports/balance-sheet/export';
    return download(path, reportQuery({ ...query, format }), `${reportCode}.${format}`);
  },
};
